import {
  getOneOffTaxFromEquipments,
  getUniqueItemsByKey,
  invokeProvidedBrandSpecificCode,
  IVehicleConfiguration,
  MAKE,
} from 'next-common';
import { IDropdownOption } from 'types';
import { serviceContractDefault } from '../sales';
import { findCorrectVatCode, INextArticle, IOrder } from 'next-common';
import { cloneDeep, isEmpty } from 'lodash';
import { ArticleType } from 'sales-types-common';
import { DEFAULT_TAX, IOverriddenTaxAndUsageCode } from 'src/common';
import { isValidOverriddenCharges, articlesTypesToUpdate, getOtherFeeTotal } from 'src/utils';

export const getServiceContractOptions = (contractTypeList: any[], selected): IDropdownOption<number>[] => {
  return getUniqueItemsByKey(contractTypeList, 'name').map((item) => ({
    isSelected: item.id === selected,
    label: `${item.name}`,
    value: item.id,
  }));
};

export const getServiceContractDurationOptions = (
  yearList: any[],
  selectedValue: number
): IDropdownOption<string>[] => {
  return yearList.map((item) => ({
    isSelected: item === selectedValue / 12,
    label: `${item} år`,
    value: item,
  }));
};

export const getServiceContractDistanceOptions = (
  kiloMeterList: any[],
  selectedValue: number
): IDropdownOption<string>[] => {
  return kiloMeterList.map((item) => ({
    isSelected: item === selectedValue,
    label: `${item} km`,
    value: item,
  }));
};

export const getCarTypeOptions = (usageFactorDataList: any[], selected: string): IDropdownOption<string>[] => {
  const selectedType = selected == '' ? serviceContractDefault.usageType : selected;
  return usageFactorDataList.map((item) => ({
    isSelected: item.name === selectedType,
    label: `${item.name}`,
    value: item.id,
  }));
};

export const getPaymentPeriodDataListOptions = (
  paymentPeriodDataList: any[],
  selected: string
): IDropdownOption<string>[] => {
  return paymentPeriodDataList?.map((item) => ({
    isSelected: item.name === selected,
    label: `${item.name}`,
    value: item.id,
  }));
};

export const mapArticlesForUsageCode = (
  order: IOrder,
  vehicle: IVehicleConfiguration,
  overriddenTaxAndUsageCodeDetails: IOverriddenTaxAndUsageCode,
  articles: INextArticle[]
) => {
  const existingArticles = cloneDeep(articles);
  if (!existingArticles?.length) return [];
  const { usageCode, overriddenVehicleRoadTax } = overriddenTaxAndUsageCodeDetails;
  const mappedArticles = existingArticles?.map((article) => {
    if (articlesTypesToUpdate.includes(article?.articleType)) {
      if (!isEmpty(usageCode)) {
        return {
          ...article,
          vatCode: findCorrectVatCode({
            usageCode: usageCode,
            articleType: article?.articleType,
          }),
        };
      }
      return article;
    } else if (article?.articleType === ArticleType.TAREWEIGHT) {
      if (isValidOverriddenCharges(overriddenTaxAndUsageCodeDetails)) {
        if (overriddenVehicleRoadTax <= DEFAULT_TAX.NON_ZERO) {
          return {
            ...article,
            priceExclVat: 0,
          };
        }
        return {
          ...article,
          priceExclVat:
            overriddenVehicleRoadTax - getOtherFeeTotal(order?.pimSnapshot, overriddenTaxAndUsageCodeDetails),
        };
      }

      if (invokeProvidedBrandSpecificCode(MAKE.Kia, { vehicleConfiguration: vehicle })) {
        return {
          ...article,
          priceExclVat:
            getOneOffTaxFromEquipments(cloneDeep(vehicle), []) ??
            order?.pimSnapshot?.oneOffTaxWeight ??
            vehicle?.pricing?.oneOffTaxWeight ??
            0,
        };
      }
      return {
        ...article,
        priceExclVat: order?.pimSnapshot?.oneOffTaxWeight ?? vehicle?.pricing?.oneOffTaxWeight ?? 0,
      };
    } else if (article?.articleType === ArticleType.SCRAPDEPOSIT) {
      if (isValidOverriddenCharges(overriddenTaxAndUsageCodeDetails)) {
        if (overriddenVehicleRoadTax < DEFAULT_TAX.NON_ZERO) {
          return { ...article, priceExclVat: DEFAULT_TAX.ZERO };
        } else if (overriddenVehicleRoadTax >= DEFAULT_TAX.NON_ZERO) {
          return { ...article, priceExclVat: DEFAULT_TAX.NON_ZERO };
        }
      }
      return {
        ...article,
        priceExclVat: order?.pimSnapshot?.extraWreckage ?? vehicle?.pricing?.extraWreckage,
      };
    } else if (article?.articleType === ArticleType.CO2TAX || article?.articleType === ArticleType.NOXTAX) {
      const priceExclVat =
        article?.articleType === ArticleType.CO2TAX ? order?.pimSnapshot?.co2Tax : order?.pimSnapshot?.noxTax;
      if (isValidOverriddenCharges(overriddenTaxAndUsageCodeDetails)) {
        if (overriddenVehicleRoadTax <= DEFAULT_TAX.NON_ZERO) {
          return {
            ...article,
            priceExclVat: 0,
          };
        }
      }
      return {
        ...article,
        priceExclVat,
      };
    }
    return article;
  });

  const shouldAddMissingTaxArticles =
    !isValidOverriddenCharges(overriddenTaxAndUsageCodeDetails) &&
    (order?.pimSnapshot?.co2Tax > 0 || order?.pimSnapshot?.noxTax > 0);

  if (shouldAddMissingTaxArticles) {
    const taxArticlesToAdd: { type: ArticleType; priceExclVat: number }[] = [
      { type: ArticleType.CO2TAX, priceExclVat: order?.pimSnapshot?.co2Tax },
      { type: ArticleType.NOXTAX, priceExclVat: order?.pimSnapshot?.noxTax },
    ];

    taxArticlesToAdd.forEach(({ type, priceExclVat }) => {
      const articleExists = existingArticles.some((article) => article.articleType === type);
      if (!articleExists) {
        mappedArticles.push({
          articleType: type,
          priceExclVat,
          vatCode: findCorrectVatCode({ usageCode, articleType: type }),
        } as INextArticle);
      }
    });
  }
  return mappedArticles;
};

export const getBruksCodeDetailsVariables = (overriddenTaxAndUsageCodeDetails: IOverriddenTaxAndUsageCode) => {
  return {
    chargesAreOverridden: overriddenTaxAndUsageCodeDetails?.chargesAreOverridden,
    vatCalculation: {
      overriddenVehicleRoadTax: overriddenTaxAndUsageCodeDetails?.overriddenVehicleRoadTax ?? null,
    },
    pimSnapshot: {
      vehicleUsageCode: overriddenTaxAndUsageCodeDetails?.usageCode,
    },
  };
};
